import { DSelect } from "@/components/common/DeskSelect";
import { Fragment, useEffect, useRef, useState } from "react";
import { toIsoOrNull } from "@/lib/dates";
import { X, Search, Send, Flame, Zap, Minus, Plus, Circle, Upload, Paperclip, Calendar } from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { J3Select } from "@/components/common/ProjectJ3";
import { useAuth } from "@/context/AuthContext";
import { AVI_SUBTYPES } from "@/components/icons/AviSubtypeIcons";
import useEscapeClose from "@/hooks/useEscapeClose";
import ModuleDialog from "@/components/shared/ModuleDialog";
// Spec iter132 pt.2 (Feb 2026) — capitalisation auto de la 1ère lettre
// sur tous les champs de saisie texte AVI (comme Tâches / Documents).
import { withFirstLetterCapitalized } from "@/lib/autoCapitalize";
import UserAvatar from "@/components/ui/UserAvatar";
import { getFileTypeMeta } from "@/lib/fileTypeMeta";
import { useRequiredFields, requiredStyle } from "@/lib/useRequiredFields";
import { FieldError, MissingSummary } from "@/components/common/RequiredField";
// Spec iter172 (2026-02-27) — Shadcn Select à la place du <DSelect> natif.
import {
  Select as ShadSelect,
  SelectContent as ShadSelectContent,
  SelectItem as ShadSelectItem,
  SelectTrigger as ShadSelectTrigger,
  SelectValue as ShadSelectValue,
} from "@/components/ui/select";

/**
 * ReminderCounter — Pilule -/+ inline pour un compteur (heures / jours).
 * Spec iter171 pt.2 (2026-02-27) — style pill 34px inspiré du
 * DurationPicker des tâches, cercle coloré (bleu pour heures, gris pour
 * jours). Bouton +/- de 22px inside la pill, valeur centrée.
 * Spec iter197 pt.1 (2026-02-28) — Le chiffre central est désormais un
 * vrai `<input type="number">` (cliquable, saisie clavier directe),
 * plus une simple `<span>` statique. Spin-buttons masqués via CSS.
 */
function ReminderCounter({ value, onChange, label, max = 999, accent = "#9CA3AF", active = false, testid }) {
  const v = Number(value) || 0;
  const dec = () => onChange(Math.max(0, v - 1));
  const inc = () => onChange(Math.min(max, v + 1));
  const handleInputChange = (e) => {
    const raw = e.target.value;
    if (raw === "") { onChange(0); return; }
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return;
    onChange(Math.max(0, Math.min(max, n)));
  };
  return (
    <div className="flex items-center gap-1.5">
      <div
        className="inline-flex items-center rounded-full"
        style={{
          height: 34,
          padding: "0 4px",
          gap: 6,
          background: "#212121",
          border: `1px solid ${active ? `${accent}55` : "#2A2A2A"}`,
          transition: "border-color .15s",
        }}
        data-testid={testid}
      >
        <button
          type="button"
          onClick={dec}
          aria-label={`Diminuer ${label}`}
          className="inline-flex items-center justify-center rounded-full transition-transform active:scale-95 hover:bg-white/[0.06]"
          style={{ width: 22, height: 22, background: "transparent", color: "#9CA3AF" }}
          data-testid={`${testid}-dec`}
        ><Minus size={12} strokeWidth={2.8} /></button>
        <input
          type="number"
          min={0}
          max={max}
          value={v}
          onChange={handleInputChange}
          onFocus={(e) => e.target.select()}
          onKeyDown={(e) => {
            if (e.key === "ArrowUp") { e.preventDefault(); inc(); }
            if (e.key === "ArrowDown") { e.preventDefault(); dec(); }
          }}
          className="tabular-nums font-bold text-center bg-transparent outline-none reminder-counter-input"
          style={{
            width: 36,
            fontSize: 15,
            color: v > 0 ? accent : "#6B7280",
            border: "none",
            padding: 0,
            appearance: "textfield",
            MozAppearance: "textfield",
          }}
          data-testid={`${testid}-value`}
          aria-label={label}
        />
        <button
          type="button"
          onClick={inc}
          aria-label={`Augmenter ${label}`}
          className="inline-flex items-center justify-center rounded-full transition-transform active:scale-95 hover:bg-white/[0.06]"
          style={{ width: 22, height: 22, background: "transparent", color: "#9CA3AF" }}
          data-testid={`${testid}-inc`}
        ><Plus size={12} strokeWidth={2.8} /></button>
      </div>
      <span className="text-[11px]" style={{ color: active ? accent : "#9CA3AF" }}>{label}</span>
    </div>
  );
}

// Spec iter171 pt.9 (2026-02-27) — Avatar rond + nom uniquement (sans
// email) dans tous les formulaires AVI, style aligné sur le reste de l'app.
function MiniAvatar({ user, size = 22 }) {
  return (
    <span className="inline-flex rounded-full flex-shrink-0" style={{ border: "1.5px solid #161616" }}>
      <UserAvatar user={user || { first_name: "?", last_name: "" }} size={size} showPopover={false} />
    </span>
  );
}

/**
 * AviCreateDialog — spec §5 (rev-avi-1, 2026-02-07).
 * Formulaire de création d'une demande AVI (Validation).
 *
 * Spec rev169 (2026-02) — ajustements layout + comportement :
 *   1. Ordre des champs : Siège / Projet AVANT Objet.
 *   2. Boutons priorité : icône + libellé, actif = fond plein sémantique
 *      + inset shadow 1.5 px pour renforcer la sélection.
 *   3. Zone d'upload dashed (drag&drop + parcourir) entre Description et
 *      Destinataires. La pièce jointe reste locale (envoi B2 en itér 5).
 *   4. Fermeture au clavier via Échap.
 */
const PRIORITIES = [
  { key: "immediat", label: "Immédiat", Icon: Flame,  color: "#EF4444", activeBg: "#7A2A2A", accent: "#E07A7A" },
  { key: "urgent",   label: "Urgent",   Icon: Zap,    color: "#E0A55A", activeBg: "#3A2D18", accent: "#E0A55A" },
  { key: "moyen",    label: "Moyen",    Icon: Minus,  color: "#8B8B90", activeBg: "#2A2A2E", accent: "#B0B0B5" },
  { key: "normal",   label: "Normal",   Icon: Circle, color: "#5AB87A", activeBg: "#1E3A24", accent: "#5AB87A" },
];

export default function AviCreateDialog({ subtype, onClose, onCreated }) {
  const { user } = useAuth();
  const meta = AVI_SUBTYPES[subtype];
  // Spec iter144 Phase 3 (Feb 2026) — rôle actif polymorphe selon le
  // sous-type. Sur Accusé, on manipule des « réceptionnaires » ; sur
  // Validation, des « décideurs ». Le rôle "temoin" reste identique.
  const isAccuse = subtype === "accuse";
  const isInfo = subtype === "information";
  const activeRole = isInfo ? "lecteur" : isAccuse ? "receptionnaire" : "decideur";
  const activeLabel = isInfo ? "Lecteur" : isAccuse ? "Réceptionnaire" : "Décideur";
  const activeLabelPlural = isInfo ? "Lecteurs" : isAccuse ? "Réceptionnaires" : "Décideurs";
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("");
  const [projectId, setProjectId] = useState("");
  // Spec iter142 Section 2 (Feb 2026) — deadline optionnelle (format YYYY-MM-DD).
  const [deadline, setDeadline] = useState("");
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  // Spec iter84 (Feb 2026) — feedback explicite de l'état du chargement
  // pour éviter le "trou noir" quand l'API échoue silencieusement (bug
  // production rapporté). Trois états : 'loading' | 'ready' | 'error'.
  const [usersState, setUsersState] = useState("loading");
  const [participants, setParticipants] = useState([]); // {user_id, role}
  const [userSearch, setUserSearch] = useState("");
  const [userInputFocused, setUserInputFocused] = useState(false);
  const [saving, setSaving] = useState(false);
  // Spec rev158 (2026-02-07) — Support multi-pièces jointes.
  // Passage d'un seul fichier à un tableau. Drag&drop + input file
  // acceptent maintenant `multiple`. Upload séquentiel vers B2 via
  // POST /avi/{id}/attachments après création. Les échecs partiels
  // sont notifiés fichier par fichier.
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Spec iter171 pt.2 (2026-02-27) — Rappel Information à 3 modes
  // affichés simultanément. L'utilisateur remplit UN seul mode :
  //   • HEURES (default 6, pilule −/+ cercle bleu)
  //   • JOURS (default 0, pilule −/+ cercle gris)
  //   • DATE + HEURE absolue (jj/mm/aaaa HH:MM)
  // Le mode "actif" est déterminé par la valeur (heures>0, jours>0,
  // ou date renseignée). Priorité de résolution côté backend :
  // date > jours > heures.
  const [reminderHours, setReminderHours] = useState(0);
  const [reminderDays, setReminderDays] = useState(0);
  const [reminderDate, setReminderDate] = useState(""); // ISO datetime-local
  // Spec iter179 pt.4 (2026-02-27) — ref sur l'input date pour ouvrir
  // le picker natif via un clic sur l'icône Calendar dédiée.
  const _reminderDateRef = useRef(null);
  const reminderMode = reminderDate ? "absolute"
    : (Number(reminderDays) > 0 ? "days"
    : (Number(reminderHours) > 0 ? "hours" : null));

  useEffect(() => {
    api.get("/projects").then((r) => setProjects((r.data.items || r.data || []).filter((p) => p.in_choices !== false))).catch(() => {}); // iter419 pt 3
    // Spec iter84 (Feb 2026) — expose l'état de chargement des users
    // pour donner un feedback explicite si l'API échoue (auparavant
    // `.catch(()=>{})` avalait silencieusement l'erreur → l'utilisateur
    // voyait un champ Destinataires "sans utilisateurs" sans message,
    // apparaissant comme bloquant).
    setUsersState("loading");
    api.get("/users/directory")
      .then((r) => {
        const list = r.data.items || r.data || [];
        setUsers(list);
        setUsersState("ready");
      })
      .catch((err) => {
        setUsersState("error");
        // eslint-disable-next-line no-console
        console.error("[AviCreateDialog] Failed to load /users:", err);
        toast.error("Impossible de charger les utilisateurs", {
          description: err.response?.data?.detail || err.message || "Vérifiez votre connexion.",
        });
      });
  }, []);

  // Spec rev169 (2026-02) point 4 → rev171 : Échap ferme la modale via
  // le hook partagé `useEscapeClose` (pile LIFO globale : ferme la
  // modale du dessus, pas plusieurs à la fois).
  useEscapeClose(onClose);

  const hasDecider = participants.some((p) => p.role === activeRole);
  const canSubmit = subject.trim() && priority && hasDecider && !saving;

  // Spec iter139 pt.1 (Feb 2026) — feedback inline pour les champs
  // obligatoires manquants, même emplacement/style que le formulaire
  // Documents (petit texte rouge au-dessus du bouton d'envoi). Le hint
  // apparaît UNIQUEMENT quand `canSubmit === false` ET qu'au moins un
  // champ obligatoire est vide. On liste les champs manquants pour
  // que l'utilisateur sache exactement quoi corriger.
  const req = useRequiredFields([
    { key: "subject", label: "Objet", ok: !!subject.trim() },
    { key: "priority", label: "Priorité", ok: !!priority },
    { key: "recipients", label: `Destinataires / ${activeLabel}`, ok: hasDecider },
  ]);

  const toggleRole = (uid) => {
    setParticipants((prev) => prev.map((p) => (
      p.user_id === uid ? { ...p, role: p.role === activeRole ? "temoin" : activeRole } : p
    )));
  };

  const addUser = (u) => {
    // Spec §4.1 — demandeur pas dans les décideurs/témoins/réceptionnaires.
    if (u.id === user.id) {
      toast.error("Vous ne pouvez pas vous ajouter", {
        description: isAccuse
          ? "Vous ne pouvez pas être votre propre réceptionnaire."
          : "Utilisez le type Information si vous êtes votre propre décideur.",
      });
      return;
    }
    if (participants.some((p) => p.user_id === u.id)) return;
    setParticipants((prev) => [...prev, { user_id: u.id, role: activeRole }]);
    setUserSearch("");
  };

  const removeParticipant = (uid) => {
    setParticipants((prev) => prev.filter((p) => p.user_id !== uid));
  };

  const submit = async () => {
    if (saving || !req.validate()) return;
    setSaving(true);
    try {
      const { data: created } = await api.post("/avi", {
        avi_type: subtype,
        subject: subject.trim(),
        description: description.trim() || null,
        priority,
        project_id: projectId || null,
        // Spec iter142 Sect. 2 (Feb 2026) — deadline optionnelle.
        deadline: deadline || null,
        participants,
        attachments: [],
        // Spec iter171 pt.2 (2026-02-27) — Rappel 3-modes.
        // Spec iter195 (2026-02-28) — étendu aux 3 sous-types
        // (Validation, Accusé, Information). Les champs conservent le
        // préfixe historique `info_reminder_` — rétro-compatible.
        info_reminder_hours: reminderMode === "hours" ? Math.max(1, Number(reminderHours) || 0) : null,
        info_reminder_days: reminderMode === "days" ? Math.max(1, Number(reminderDays) || 0) : null,
        info_reminder_absolute_at: reminderMode === "absolute" ? toIsoOrNull(reminderDate) : null,
        info_reminder_enabled: Boolean(reminderMode),
      });
      // Spec rev158 (2026-02-07) — upload B2 séquentiel des N fichiers.
      // La création AVI est traitée en priorité : si un upload échoue,
      // on notifie sans bloquer les suivants et la demande reste
      // valide (elle existe déjà en base).
      if (attachedFiles.length > 0 && created?.id) {
        let okCount = 0;
        const failed = [];
        for (const f of attachedFiles) {
          try {
            const form = new FormData();
            form.append("file", f);
            await api.post(`/avi/${created.id}/attachments`, form, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            okCount++;
          } catch (upErr) {
            failed.push({ name: f.name, err: upErr?.response?.data?.detail || "" });
          }
        }
        if (failed.length === 0) {
          toast.success(`Demande envoyée · ${okCount} pièce${okCount > 1 ? "s" : ""} jointe${okCount > 1 ? "s" : ""}`);
        } else if (okCount > 0) {
          toast.warning(`Demande envoyée, ${failed.length}/${attachedFiles.length} pièce${failed.length > 1 ? "s" : ""} en échec`, {
            description: failed.map((f) => f.name).join(", "),
          });
        } else {
          toast.warning("Demande envoyée, mais aucune pièce jointe n'a pu être uploadée", {
            description: failed[0]?.err || "Réessaie depuis le détail de la demande.",
          });
        }
      } else {
        toast.success("Demande envoyée");
      }
      onCreated?.();
    } catch (e) {
      toast.error("Échec de l'envoi", { description: e?.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter((u) => {
    if (u.id === user.id) return false;
    if (participants.some((p) => p.user_id === u.id)) return false;
    // Spec iter146 fix (Feb 2026) — n'expose que les comptes assignables
    // (active + invited). "blocked" et "inactive" ne peuvent pas répondre.
    if (u.status === "blocked" || u.status === "inactive" || u.is_deleted || u.selectable === false) return false;
    if (!userSearch) return true;
    const s = `${u.first_name || ""} ${u.last_name || ""} ${u.email || ""}`.toLowerCase();
    return s.includes(userSearch.toLowerCase());
  }).slice(0, 8);

  const usersById = users.reduce((acc, u) => { acc[u.id] = u; return acc; }, {});

  return (
    <ModuleDialog
      open={true}
      onOpenChange={(v) => { if (!v) onClose(); }}
      title={`Nouvelle ${meta.label}`}
      description=""
      moduleColor={meta.color}
      moduleIcon={<meta.Icon size={20} />}
      maxWidth="max-w-2xl"
      testId="avi-create-dialog"
      stickyContent={(
        /* Spec iter164Q (2026-02-27) — Bloc RÉFÉRENCE / CRÉÉE LE sous
            le titre : pastille pleine couleur sous-type à gauche
            (VAL0001/26, ACC0001/26, INF0001/26 placeholder next-id) +
            date+heure de création à droite. Non modifiable.
            Le vrai numéro est attribué à la sauvegarde côté backend.
            Spec iter208 (2026-02-28) — passé en `stickyContent` de
            ModuleDialog pour rester figé pendant le scroll. */
        <div className="flex items-center justify-between px-4 py-2.5 rounded-lg"
             style={{ background: "#0F0F0F", border: `1px solid ${meta.color}44` }}
             data-testid="avi-create-refblock">
          <div className="flex items-center gap-2">
            <span className="text-[9px] uppercase tracking-widest text-muted-foreground">Référence</span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold tracking-wide"
                  style={{ background: meta.color, color: "#FFFFFF" }}
                  data-testid="avi-create-ref-badge">
              {meta.refPrefix}{"0000"}/{new Date().getFullYear().toString().slice(-2)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[9px] uppercase tracking-widest text-muted-foreground">Créée le</span>
            <span className="text-xs font-medium" style={{ color: "#EDEDED" }} data-testid="avi-create-created-at">
              {new Date().toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          </div>
        </div>
      )}
      footer={(
        <div className="space-y-2">
          <MissingSummary errors={req.errors} testId="avi-create-missing-hint" />
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            data-incomplete={!canSubmit || undefined}
            className="w-full h-11 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold text-white transition-all"
            style={{
              background: canSubmit ? meta.color : "#2A2A2A",
              boxShadow: canSubmit ? `0 4px 14px ${meta.color}55` : "none",
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
            data-testid="avi-create-submit"
          >
            <Send size={16} strokeWidth={2.4} /> {saving ? "Envoi…" : (isInfo ? "Envoyer l'information" : "Envoyer la demande")}
          </button>
        </div>
      )}
    >
      <div className="space-y-5">
        {/* Spec iter142 Sect. 2 (Feb 2026) — Siège/Projet + Deadline
            (optionnelle) sur la ligne 1. La Priorité passe en ligne 2
            (4 boutons compacts). */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Siège / Projet</label>
              {/* Spec iter172 (2026-02-27) — Shadcn Select à la place du
                  <DSelect> HTML natif pour aligner l'UX avec le reste des
                  formulaires (Documents / Tâches). */}
              <div className="mt-1"><J3Select projects={projects} value={projectId} onChange={(v) => setProjectId(v || "")} clearLabel="— Aucun —" placeholder="— Aucun —" testId="avi-create-project" triggerStyle={{ height: 40, background: "#212121", border: "1px solid #383838" }} /></div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Deadline <span className="normal-case font-normal opacity-70">(optionnelle)</span>
              </label>
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full h-10 px-2 mt-1 rounded-md text-sm appearance-none"
                style={{
                  background: "#212121",
                  border: `1px solid ${deadline ? meta.color : "#383838"}`,
                  color: deadline ? meta.color : "#EDEDED",
                  colorScheme: "dark",
                }}
                data-testid="avi-create-deadline"
              />
            </div>
          </div>
          {/* Spec iter171 pt.2 (2026-02-27) — Rappel automatique :
              3 modes affichés simultanément sur une seule ligne
              (heures / jours / date absolue). L'utilisateur remplit
              UN seul mode ; les autres restent à zéro/vide.
              Spec iter195 (2026-02-28) — étendu aux 3 sous-types
              (Validation violet #5B5FC7, Accusé orange #F9A826,
              Information vert). La couleur s'adapte automatiquement
              via `meta.color`. */}
          <div
              className="rounded-md p-3 relative overflow-hidden"
              style={{ background: "#181818", border: "1px solid #2A2A2A" }}
              data-testid="avi-create-info-reminder-block"
            >
              {/* Spec iter197 pt.1 (2026-02-28) — le halo radial derrière
                  le titre a été retiré : titre en couleur du module,
                  texte plat, sans glow. */}
              <div className="text-center text-[11px] font-semibold mb-2.5" style={{ color: meta.color }}>
                Rappel si non lu après
              </div>
              <div className="flex items-center justify-center gap-3 relative">
                {/* --- HEURES --- */}
                <ReminderCounter
                  value={reminderHours}
                  onChange={(v) => { setReminderHours(v); if (v > 0) { setReminderDays(0); setReminderDate(""); } }}
                  label="heures"
                  max={168}
                  accent={meta.color}
                  active={reminderMode === "hours"}
                  testid="avi-reminder-hours"
                />
                {/* --- JOURS --- */}
                <ReminderCounter
                  value={reminderDays}
                  onChange={(v) => { setReminderDays(v); if (v > 0) { setReminderHours(0); setReminderDate(""); } }}
                  label="jours"
                  max={90}
                  accent={meta.color}
                  active={reminderMode === "days"}
                  testid="avi-reminder-days"
                />
                {/* Spec iter179 pt.4 (2026-02-27) — fine ligne verticale
                    grise qui sépare le groupe heures/jours du champ
                    date absolue. */}
                <span
                  aria-hidden
                  className="inline-block"
                  style={{ width: 1, height: 28, background: "#2A2A2A" }}
                />
                {/* --- DATE — vrai `<input type="datetime-local">`
                    visible et cliquable (saisie clavier ou sélecteur
                    natif). Spec iter197 pt.1 (2026-02-28) : plus de
                    span statique, le champ lui-même est le champ de
                    saisie. L'icône calendrier à droite est un
                    raccourci pour ouvrir le sélecteur natif. */}
                {(() => {
                  const dateInputRef = _reminderDateRef;
                  const active = reminderMode === "absolute";
                  return (
                    <div className="flex items-center gap-1.5">
                      <input
                        ref={dateInputRef}
                        type="datetime-local"
                        value={reminderDate}
                        onChange={(e) => { setReminderDate(e.target.value); if (e.target.value) { setReminderHours(0); setReminderDays(0); } }}
                        className="tabular-nums text-[12px] rounded reminder-date-input"
                        style={{
                          height: 28,
                          padding: "0 8px",
                          background: "#212121",
                          border: `1px solid ${active ? `${meta.color}55` : "#2A2A2A"}`,
                          color: active ? meta.color : "#9CA3AF",
                          colorScheme: "dark",
                          minWidth: 160,
                        }}
                        data-testid="avi-reminder-date"
                        aria-label="Date de rappel"
                      />
                      <button
                        type="button"
                        onClick={() => dateInputRef.current?.showPicker?.() || dateInputRef.current?.focus()}
                        aria-label="Choisir une date"
                        className="inline-flex items-center justify-center rounded-md transition-all hover:brightness-110"
                        style={{
                          width: 28, height: 28,
                          background: active ? meta.color : `${meta.color}22`,
                          border: `1px solid ${active ? meta.color : `${meta.color}55`}`,
                          color: active ? "#FFFFFF" : meta.color,
                        }}
                        data-testid="avi-reminder-date-btn"
                      >
                        <Calendar size={14} strokeWidth={2.4} />
                      </button>
                      <span className="text-[11px]" style={{ color: active ? meta.color : "#9CA3AF" }}>date</span>
                    </div>
                  );
                })()}
              </div>
              <div className="text-[10px] text-muted-foreground italic mt-2 text-center">
                {reminderMode
                  ? "Envoyé au créateur uniquement (email + in-app)"
                  : "Optionnel — laissez tous les champs à zéro pour désactiver"}
              </div>
            </div>
          <div data-field="priority" tabIndex={-1} style={req.has("priority") ? { border: "1px solid #e5484d", borderRadius: 8, padding: 8, margin: -8 } : undefined}>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Priorité *</label>
            <FieldError show={req.has("priority")} testId="avi-create-priority-error" />
              {/* Spec rev169 (2026-02) point 2 — icône au-dessus du libellé,
                  état actif = fond plein sémantique + inset shadow. */}
              <div className="grid grid-cols-4 gap-1 mt-1">
                {PRIORITIES.map((p) => {
                  const isActive = priority === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setPriority(p.key)}
                      className="h-14 rounded-md text-[10.5px] font-semibold transition-all flex flex-col items-center justify-center gap-1"
                      style={{
                        background: isActive ? p.activeBg : "#141417",
                        border: `1.5px solid ${isActive ? p.color : "#232327"}`,
                        boxShadow: isActive ? `inset 0 0 0 1.5px ${p.accent}` : "none",
                        color: isActive ? "#FFFFFF" : "#C8C8CC",
                      }}
                      data-testid={`avi-create-priority-${p.key}`}
                    >
                      <p.Icon size={14} strokeWidth={2.2} style={{ color: isActive ? "#FFFFFF" : p.color }} />
                      <span>{p.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          {/* Objet — désormais après Siège/Projet */}
          <div data-field="subject">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Objet *</label>
            <input
              type="text"
              value={subject}
              onChange={withFirstLetterCapitalized((e) => setSubject(e.target.value))}
              placeholder="Sujet de la demande…"
              className="w-full h-10 px-3 mt-1 rounded-md text-sm placeholder:text-[#666]"
              style={{ background: "#212121", border: "1px solid #383838", color: "#EDEDED", ...requiredStyle(req.has("subject")) }}
              data-testid="avi-create-subject"
            />
            <FieldError show={req.has("subject")} testId="avi-create-subject-error" />
          </div>
          {/* Description — Spec iter142 (Feb 2026) — champ agrandi. */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Description</label>
            <textarea value={description} onChange={withFirstLetterCapitalized((e) => setDescription(e.target.value))}
              placeholder="Détails de la demande…" rows={5}
              className="w-full mt-1 p-3 rounded-md text-sm placeholder:text-[#666] resize-y"
              style={{ background: "#212121", border: "1px solid #383838", color: "#EDEDED", minHeight: 140 }}
              data-testid="avi-create-description"
            />
          </div>
          {/* Spec rev158 (2026-02-07) — zone d'upload multi-fichiers.
              Spec iter177 pt.2 (2026-02-27) — trombone dans cercle teinté
              module, lien "parcourir" en couleur module, fichiers listés
              en blocs gris avec icône typée (FileTypeBadge). */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Pièces jointes {attachedFiles.length > 0 && (
                <span style={{ color: meta.color }}>({attachedFiles.length})</span>
              )}
            </label>
            <div
              onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const files = Array.from(e.dataTransfer?.files || []);
                if (files.length) setAttachedFiles((prev) => [...prev, ...files]);
              }}
              className="mt-1 flex items-center gap-3 rounded-lg text-sm transition-colors"
              style={{
                background: dragOver ? `${meta.color}14` : "#212121",
                border: `1px solid ${dragOver ? `${meta.color}66` : "#383838"}`,
                borderRadius: 8,
                padding: 14,
                color: "#C8C8CC",
              }}
              data-testid="avi-create-dropzone"
            >
              <span
                className="inline-flex items-center justify-center rounded-full flex-shrink-0"
                style={{
                  width: 32, height: 32,
                  background: `${meta.color}22`,
                  border: `1px solid ${meta.color}55`,
                  color: meta.color,
                }}
              >
                <Paperclip size={16} strokeWidth={2.2} />
              </span>
              <span className="leading-tight">
                Glisser un ou plusieurs fichiers ici ou{" "}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="underline font-semibold"
                  style={{ color: meta.color }}
                  data-testid="avi-create-attach-browse"
                >
                  parcourir
                </button>
              </span>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length) setAttachedFiles((prev) => [...prev, ...files]);
                  e.target.value = ""; // pour permettre de re-sélectionner
                }}
                data-testid="avi-create-attach-input"
              />
            </div>
            {/* Liste des fichiers ajoutés — bloc gris avec icône typée
                colorée (PDF rouge, Word bleu, Tableur vert, etc.) + croix. */}
            {attachedFiles.length > 0 && (
              <ul className="mt-2 space-y-1.5" data-testid="avi-create-attach-list">
                {attachedFiles.map((f, i) => {
                  const ft = getFileTypeMeta(f.name);
                  const FIcon = ft.Icon;
                  return (
                    <li
                      key={`${f.name}-${i}`}
                      className="flex items-center gap-2 rounded-lg text-sm"
                      style={{
                        background: "#212121", border: "1px solid #2A2A2A",
                        color: "#EDEDED", padding: "8px 12px",
                      }}
                      data-testid={`avi-create-attach-item-${i}`}
                    >
                      <span
                        className="inline-flex items-center justify-center rounded-md flex-shrink-0"
                        style={{
                          width: 26, height: 26,
                          background: ft.bg, color: ft.iconColor,
                        }}
                      >
                        <FIcon size={14} strokeWidth={2} />
                      </span>
                      <span className="flex-1 truncate">{f.name}</span>
                      <span className="text-[11px] text-muted-foreground tabular-nums">
                        {(f.size / 1024).toFixed(1)} Ko
                      </span>
                      <button
                        type="button"
                        onClick={() => setAttachedFiles((prev) => prev.filter((_, k) => k !== i))}
                        className="text-muted-foreground hover:text-red-400"
                        data-testid={`avi-create-attach-remove-${i}`}
                        aria-label="Retirer cette pièce jointe"
                      >
                        <X size={14} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {/* Destinataires — Spec iter142 finalisation Sect. 2 (Feb 2026) :
              chips participants + zone pointillée violet cliquable en
              dessous. */}
          <div data-field="recipients" tabIndex={-1} style={req.has("recipients") ? { border: "1px solid #e5484d", borderRadius: 8, padding: 8, margin: -8, boxShadow: "0 0 0 1px #e5484d55" } : undefined}>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Destinataires * <span className="text-muted-foreground normal-case">(au moins 1 {activeLabel.toLowerCase()})</span>
            </label>
            <FieldError show={req.has("recipients")} message={`Champ obligatoire — ajoutez au moins 1 ${activeLabel.toLowerCase()}`} testId="avi-create-recipients-error" />
            <div className="mt-2 space-y-2" data-testid="avi-participants">
              {participants.map((p) => {
                const u = usersById[p.user_id];
                const isDecider = p.role === activeRole;
                // Spec iter179 pt.3 (2026-02-27) — Information : rôle
                // Témoin supprimé. Le chip « Doit lire » devient redondant
                // (un seul type de destinataire possible) : on le retire.
                const chipLabel = isAccuse
                  ? (isDecider ? "Doit accuser" : "Témoin")
                  : (isDecider ? "Doit valider" : "Témoin");
                return (
                  <div key={p.user_id} className="flex items-center gap-2 rounded-md px-3 py-2"
                    style={{ background: "#212121", border: "1px solid #383838" }}
                    data-testid={`avi-participant-${p.user_id}`}>
                    <MiniAvatar user={u} size={26} />
                    <span className="flex-1 text-sm font-medium truncate">
                      {u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.first_name || u.last_name || "—" : p.user_id}
                    </span>
                    {!isInfo && (
                      <button type="button"
                        onClick={() => toggleRole(p.user_id)}
                        className="text-[11px] font-semibold px-3 py-1 rounded-full transition-all"
                        style={{
                          background: isDecider
                            ? (isAccuse ? "rgba(249,115,22,0.15)" : "rgba(239,68,68,0.15)")
                            : "rgba(37,99,235,0.15)",
                          border: `1px solid ${isDecider ? (isAccuse ? "#F97316" : "#EF4444") : "#2563EB"}`,
                          color: isDecider ? (isAccuse ? "#FB923C" : "#F87171") : "#60A5FA",
                          cursor: "pointer",
                        }}
                        data-testid={`avi-participant-role-${p.user_id}`}
                      >{chipLabel}</button>
                    )}
                    <button type="button" onClick={() => removeParticipant(p.user_id)}
                      className="text-muted-foreground hover:text-red-400"
                      data-testid={`avi-participant-remove-${p.user_id}`}
                    ><X size={14} /></button>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 relative">
              {/* Spec iter177 pt.3 (2026-02-27) — champ plein classique
                  (fond gris, bordure fine grise), icône loupe grise +
                  placeholder « Rechercher un collègue… », et bouton
                  carré arrondi "+" coloré module à droite. */}
              <div className="relative flex items-center gap-2">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#9CA3AF" }} />
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    onFocus={() => setUserInputFocused(true)}
                    onBlur={() => setTimeout(() => setUserInputFocused(false), 150)}
                    placeholder={
                      usersState === "loading" ? "Chargement des utilisateurs…"
                      : usersState === "error" ? "Erreur — cliquez Actualiser plus haut"
                      : "Rechercher un collègue…"
                    }
                    disabled={usersState !== "ready"}
                    className="w-full h-11 pl-10 pr-3 rounded-md text-sm placeholder:text-[#666] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    style={{
                      background: "#212121",
                      border: "1px solid #383838",
                      color: "#EDEDED",
                    }}
                    data-testid="avi-user-search"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setUserInputFocused(true)}
                  disabled={usersState !== "ready"}
                  className="inline-flex items-center justify-center transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  style={{
                    width: 44, height: 44,
                    borderRadius: 10,
                    background: meta.color,
                    color: "#FFFFFF",
                    boxShadow: `0 4px 14px ${meta.color}44`,
                  }}
                  aria-label="Ajouter un collègue"
                  title="Ajouter un collègue"
                  data-testid="avi-user-search-add"
                >
                  <Plus size={20} strokeWidth={2.6} />
                </button>
              </div>
              {/* Spec iter84 (Feb 2026) — le dropdown s'ouvre dès que
                  l'input est focus ou que l'utilisateur tape quelque
                  chose. Il affiche aussi les états vides / erreur pour
                  éviter l'impression de blocage silencieux. */}
              {(userInputFocused || userSearch) && (
                <div
                  className="mt-1 rounded-md overflow-hidden max-h-64 overflow-y-auto"
                  style={{ background: "#212121", border: "1px solid #383838" }}
                  data-testid="avi-user-suggest-panel"
                >
                  {usersState === "loading" && (
                    <div className="px-3 py-2 text-xs text-muted-foreground italic">Chargement des utilisateurs…</div>
                  )}
                  {usersState === "error" && (
                    <div className="px-3 py-2 text-xs text-red-400">
                      Erreur de chargement. Fermez puis rouvrez la modale.
                    </div>
                  )}
                  {usersState === "ready" && filteredUsers.length === 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground italic" data-testid="avi-user-suggest-empty">
                      {userSearch
                        ? `Aucun utilisateur ne correspond à « ${userSearch} ».`
                        : (users.length === 0
                          ? "Aucun autre utilisateur disponible."
                          : "Tous les utilisateurs éligibles ont été ajoutés.")}
                    </div>
                  )}
                  {usersState === "ready" && filteredUsers.map((u) => (
                    <button key={u.id} type="button" onClick={() => addUser(u)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-white/[0.03] flex items-center gap-2"
                      data-testid={`avi-user-suggest-${u.id}`}>
                      <MiniAvatar user={u} size={24} />
                      <span className="truncate">
                        {`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.first_name || u.last_name || "—"}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* Spec iter139 pt.1 (Feb 2026) — hint d'erreur des champs
              obligatoires — déplacé dans le `footer` de ModuleDialog
              (iter208). Bouton d'envoi également déplacé dans footer. */}
      </div>
    </ModuleDialog>
  );
}
